import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { publicUseCasePages } from '@/pages/publicSite/publicUseCaseContent';
import { getPublicSitePageRobots, isPublicSitePageEnabled } from '@/publicSite/publicSiteConfig';
import type { AppRouteDefinition } from './routes';

const AppFeaturesPage = lazy(async () => {
  const module = await import('@/features/features/routes');
  return { default: module.FeaturesPage };
});

const PublicFeaturesPage = lazy(async () => {
  const module = await import('../../pages/FeaturesPage');
  return { default: module.FeaturesPage };
});

const PlansPage = lazy(async () => {
  const module = await import('../../pages/PlansPage');
  return { default: module.PlansPage };
});

const UseCasesPage = lazy(async () => {
  const module = await import('../../pages/UseCasesPage');
  return { default: module.UseCasesPage };
});

const AutomatedTestReportingPage = lazy(async () => {
  const module = await import('@/pages/publicSite/useCases/AutomatedTestReportingPage');
  return { default: module.AutomatedTestReportingPage };
});

const FlakyTestTrackingPage = lazy(async () => {
  const module = await import('@/pages/publicSite/useCases/FlakyTestTrackingPage');
  return { default: module.FlakyTestTrackingPage };
});

const TestSuiteHealthPage = lazy(async () => {
  const module = await import('@/pages/publicSite/useCases/TestSuiteHealthPage');
  return { default: module.TestSuiteHealthPage };
});

const ManualTestReportingPage = lazy(async () => {
  const module = await import('@/pages/publicSite/useCases/ManualTestReportingPage');
  return { default: module.ManualTestReportingPage };
});

const publicUseCaseComponents = {
  'automated-test-reporting': AutomatedTestReportingPage,
  'flaky-test-tracking': FlakyTestTrackingPage,
  'manual-test-reporting': ManualTestReportingPage,
  'test-suite-health': TestSuiteHealthPage,
} as const;

const publicUseCaseRouteDefinitions: AppRouteDefinition[] = publicUseCasePages.map((page) => ({
  key: `use-case-${page.slug}`,
  path: page.path,
  enabled: isPublicSitePageEnabled('useCases'),
  title: page.title,
  description: page.description,
  metadata: {
    canonicalPath: page.path,
    imageAlt: page.imageAlt,
    imagePath: '/logo.png',
    robots: getPublicSitePageRobots(),
    twitterCard: 'summary',
  },
  element: () => {
    const UseCaseComponent = publicUseCaseComponents[page.slug];
    return <UseCaseComponent />;
  },
}));

const legacyUseCaseRedirects = [
  { from: '/use-cases/who-is-flowqa-for', to: '/use-cases' },
  { from: '/use-cases/test-results-history', to: '/use-cases/automated-test-reporting' },
  { from: '/use-cases/test-reporting', to: '/use-cases/automated-test-reporting' },
  { from: '/use-cases/test-quality-metrics', to: '/use-cases/test-suite-health' },
  { from: '/use-cases/spreadsheet-test-management', to: '/use-cases/manual-test-reporting' },
  { from: '/use-cases/ai-generated-test-visibility', to: '/use-cases' },
] as const;

const legacyUseCaseRouteDefinitions: AppRouteDefinition[] = legacyUseCaseRedirects.map(
  ({ from, to }) => ({
    key: `legacy-use-case-${from.replace('/use-cases/', '')}`,
    path: from,
    enabled: isPublicSitePageEnabled('useCases'),
    element: () => <Navigate to={to} replace />,
  }),
);

export const publicSiteRouteDefinitions: AppRouteDefinition[] = [
  {
    key: 'public-features',
    path: '/features',
    title: 'Features',
    description:
      'FlowQA helps teams preserve CI test results, compare recurring failures, track flaky tests, and drill from release questions into runs, suites, and tests.',
    metadata: {
      canonicalPath: '/features',
      imageAlt: 'FlowQA test result visibility and QA reporting',
      imagePath: '/logo.png',
      robots: getPublicSitePageRobots(),
      twitterCard: 'summary',
    },
    element: ({ session }) => (session ? <AppFeaturesPage /> : <PublicFeaturesPage />),
  },
  {
    key: 'plans',
    path: '/plans',
    enabled: isPublicSitePageEnabled('plans'),
    title: 'Plans',
    description:
      'Compare FlowQA Free, Solo, and Team plans for CI test result history, JUnit uploads, Playwright visibility, run capacity, retention, and QA reporting.',
    metadata: {
      canonicalPath: '/plans',
      imageAlt: 'FlowQA plans for QA reporting and test run history',
      imagePath: '/logo.png',
      robots: getPublicSitePageRobots(),
      twitterCard: 'summary',
    },
    element: () => <PlansPage />,
  },
  {
    key: 'use-cases',
    path: '/use-cases',
    enabled: isPublicSitePageEnabled('useCases'),
    title: 'Test Reporting Use Cases | FlowQA',
    description:
      'Explore FlowQA use cases for automated test reporting, flaky test tracking, test suite health metrics, and manual test reporting from CSV or XLSX.',
    metadata: {
      canonicalPath: '/use-cases',
      imageAlt: 'FlowQA use cases for CI test history and QA reporting',
      imagePath: '/logo.png',
      robots: getPublicSitePageRobots(),
      twitterCard: 'summary',
    },
    element: () => <UseCasesPage />,
  },
  ...publicUseCaseRouteDefinitions,
  ...legacyUseCaseRouteDefinitions,
];
